// Figures and facts: a big number over a small label (Stat, in a StatRow), and a label/value list
// (Facts of FactRows) with an icon in the gutter so a column of rows scans by shape.
import "./config";
import { Children, Fragment, isValidElement } from "react";
import { Text, styled } from "tamagui";
import { Icon, type IconName } from "./Icon";
import { Column, Divider, Fill, Row } from "./layout";
import { Body, Caption } from "./text";

const StatValue = styled(Text, {
  name: "StatValue",
  fontFamily: "heading",
  fontSize: "8",
  lineHeight: "8",
  fontWeight: "600",
  color: "text",
  fontVariant: ["tabular-nums"],
});

export interface StatProps {
  value: string | number;
  label: string;
}

/** A figure with its meaning under it; three of them side by side make a StatRow. */
export function Stat({ value, label }: StatProps): React.JSX.Element {
  return (
    <Column flex={1} minWidth={0} gap={2}>
      <StatValue>{value}</StatValue>
      <Caption lineHeight="3">{label}</Caption>
    </Column>
  );
}

/** Stats sharing the width equally. */
export const StatRow = styled(Row, { name: "StatRow", alignItems: "flex-start", gap: "3" });

export interface FactRowProps {
  icon?: IconName | undefined;
  label: string;
  value?: React.ReactNode;
  /** A fainter second part of the value (a unit, a year). */
  sub?: string | undefined;
  /** Replaces `value` for anything richer than text. */
  children?: React.ReactNode;
}

export function FactRow({ icon, label, value, sub, children }: FactRowProps): React.JSX.Element {
  const shown = children ?? value;
  return (
    <Row gap="3" paddingVertical="3">
      {icon === undefined ? null : <Icon name={icon} size={18} tone="textFaint" />}
      <Fill>
        <Body size="small" tone="dim" weight="medium">
          {label}
        </Body>
      </Fill>
      <Row gap="2" wrap justifyContent="flex-end" minWidth={0} flexShrink={1}>
        {typeof shown === "string" || typeof shown === "number" ? (
          <Body size="small" weight="strong" numeric textAlign="right">
            {shown}
          </Body>
        ) : (
          shown
        )}
        {sub === undefined ? null : (
          <Body size="small" tone="faint" weight="medium">
            {sub}
          </Body>
        )}
      </Row>
    </Row>
  );
}

/** A list of FactRows with a hairline between them. */
export function Facts({ children }: { children: React.ReactNode }): React.JSX.Element {
  const rows = Children.toArray(children).filter(isValidElement);
  return (
    <Column>
      {rows.map((row, index) => (
        <Fragment key={row.key ?? index}>
          {index === 0 ? null : <Divider />}
          {row}
        </Fragment>
      ))}
    </Column>
  );
}
